import React, { useEffect, useState, useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import { NotificationMessage, FileModel } from "../../../shared/models/models";
import ActionToast from "../../../shared/components/ActionToast";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { CustomerCompanyState } from "../models/models";
import { getCompany, setError, setSuccess } from "../customerCompaniesSlice";
import Loader from "../../../shared/components/Loader";
import CompanyHero from "./CompanyHero";
import CompanyMachinery from "./CompanyMachinery";
import SaveDropdown from "./SaveDropdown";
import UploadImageCrop from "../../../shared/components/UploadImageCrop";
import useTranslation from "../../../shared/hooks/useTranslation";
import {
  getCompanyGroups,
  createCompanyGroup,
  addCompanyGroupItem,
  deleteCompanyGroupItem,
} from "../../companyGroups/customerCompanyGroupsSlice";
import {
  CustomerCompanyGroupsState,
  CompanyGroup,
  CompanyGroupsQueryParameter,
} from "../../companyGroups/models/models";
import {
  getCustomer,
  editDescription,
  editLogoSocials,
  editOffice,
} from "../../settings/customerSettingsSlice";
import { CustomerSettingsState } from "../../settings/models/models";
import {
  getPartnershipWith,
  getSentInvitations,
} from "../../partnerships/customerPartnershipsSlice";
import { CustomerPartnershipsState, InvitationStatusCode, RelationshipTierCode } from "../../partnerships/models/models";
import InvitePartnerDialog from "../../partnerships/components/InvitePartnerDialog";
import { fetchReviewSummary } from "../../servicing/serviceReviewsSlice";
import { ServiceReviewsState } from "../../servicing/models/reviewModels";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";

export interface ProfileEditForm {
  description: string;
  website: string;
  linkedInLink: string;
  instagramLink: string;
  facebookLink: string;
  xLink: string;
  phone: string;
  email: string;
  addressLine: string;
  city: string;
  zipCode: string;
  latitude?: number;
  longitude?: number;
}

const Company = () => {
  const { translate } = useTranslation(["partnerHub", "partnerships"]);
  const { company, machines, machinesQuery, machineTypes, machineManufacturers, loading, success, error } =
    useAppSelector<CustomerCompanyState>((state: any) => state.customerCompanies);

  const dispatch = useAppDispatch();
  const { id } = useParams();
  const isPhone = useMediaQuery("(max-width:768px)");

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Use shared redesign setup (fonts + keyframes)
  useRedesignSetup();

  // Own company detection
  const {
    customer: ownCustomer,
  } = useAppSelector<CustomerSettingsState>((state: any) => state.customerSettings);
  const isOwnProfile = !!ownCustomer?.id && !!id && ownCustomer.id.toLowerCase() === id.toLowerCase();

  // Partnership status
  const { partnershipWith, invitationsSent } = useAppSelector<CustomerPartnershipsState>(
    (state: any) => state.customerPartnerships
  );
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  // Review summary
  const { summary: reviewSummary } = useAppSelector<ServiceReviewsState>(
    (state: any) => state.serviceReviews
  );

  const partnershipStatus = useMemo(() => {
    if (isOwnProfile || !id) return undefined;

    if (partnershipWith && partnershipWith.isActive) {
      return {
        type: "partner" as const,
        tier: partnershipWith.effectiveTier as RelationshipTierCode,
        activatedAt: partnershipWith.activatedAt,
        partnershipId: partnershipWith.id,
      };
    }

    if (invitationsSent?.items) {
      const pendingToThisCompany = invitationsSent.items.find(
        (inv) =>
          inv.inviteeCustomerId?.toLowerCase() === id.toLowerCase() &&
          inv.statusCode === InvitationStatusCode.Pending
      );
      if (pendingToThisCompany) {
        return { type: "pending" as const };
      }
    }

    return { type: "none" as const };
  }, [isOwnProfile, id, partnershipWith, invitationsSent]);

  // Edit form state
  const emptyForm: ProfileEditForm = {
    description: "",
    website: "",
    linkedInLink: "",
    instagramLink: "",
    facebookLink: "",
    xLink: "",
    phone: "",
    email: "",
    addressLine: "",
    city: "",
    zipCode: "",
    latitude: undefined,
    longitude: undefined,
  };
  const [editForm, setEditForm] = useState<ProfileEditForm>(emptyForm);

  // Logo editing state
  const [logoFile, setLogoFile] = useState<FileModel | null>(null);
  const [showLogoCrop, setShowLogoCrop] = useState(false);

  const handleEditToggle = () => {
    if (!isEditing && company) {
      setEditForm({
        description: company.description || "",
        website: company.website || "",
        linkedInLink: company.linkedInLink || "",
        instagramLink: company.instagramLink || "",
        facebookLink: company.facebookLink || "",
        xLink: company.xLink || "",
        phone: company.phone || "",
        email: company.email || "",
        addressLine: company.address?.addressLine || "",
        city: company.address?.city || "",
        zipCode: company.address?.zipCode || "",
        latitude: company.address?.latitude ? Number(company.address.latitude) : undefined,
        longitude: company.address?.longitude ? Number(company.address.longitude) : undefined,
      });
      setLogoFile(company.logo ?? null);
    }
    setIsEditing(!isEditing);
  };

  const handleFieldChange = (field: keyof ProfileEditForm, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = () => setShowLogoCrop(true);
  const handleLogoUploaded = (file: FileModel | null) => {
    setLogoFile(file);
    setShowLogoCrop(false);
  };

  const handleSave = async () => {
    if (!company) return;
    setSaving(true);
    try {
      await dispatch(editDescription(editForm.description)).unwrap();
      await dispatch(
        editLogoSocials({
          logo: logoFile,
          linkedInLink: editForm.linkedInLink,
          instagramLink: editForm.instagramLink,
          xLink: editForm.xLink,
          facebookLink: editForm.facebookLink,
        })
      ).unwrap();
      await dispatch(
        editOffice({
          website: editForm.website,
          phone: editForm.phone,
          email: editForm.email,
          defaultContactId: company.defaultContact?.id || "",
          address: editForm.addressLine,
          city: editForm.city,
          zipCode: editForm.zipCode,
          latitude: editForm.latitude,
          longitude: editForm.longitude,
        })
      ).unwrap();
      if (id) await dispatch(getCompany(id));
      dispatch(getCustomer());
      dispatch(setSuccess(translate("edit.savedSuccess")));
      setIsEditing(false);
    } catch (err: any) {
      dispatch(setError(err?.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  // Groups state
  const { companyGroups } = useAppSelector<CustomerCompanyGroupsState>(
    (state: any) => state.customerCompanyGroups
  );
  const groupsList: CompanyGroup[] = companyGroups?.items ?? [];

  const groupsQuery = {
    sortBy: CompanyGroupsQueryParameter.name,
    sortDesc: false,
    pageNo: 1,
    pageSize: 100,
  };

  const reloadGroups = () => dispatch(getCompanyGroups(groupsQuery as any));

  const isSaved = useMemo(() => {
    if (!id) return false;
    const idLower = id.toLowerCase();
    return groupsList.some((g) =>
      g.items?.some((item) => item.customer?.id?.toLowerCase() === idLower)
    );
  }, [groupsList, id]);

  const handleToggleGroup = async (groupId: string, isInGroup: boolean) => {
    if (!id) return;
    const idLower = id.toLowerCase();
    const group = groupsList.find((g) => g.id === groupId);
    if (isInGroup) {
      const item = group?.items?.find((i) => i.customer?.id?.toLowerCase() === idLower);
      if (item) {
        await dispatch(deleteCompanyGroupItem({ id: groupId, itemId: item.id }));
      }
      dispatch(setSuccess(translate("groups.removedFromGroup", { name: group?.name || "" })));
    } else {
      await dispatch(addCompanyGroupItem({ id: groupId, customerId: id }));
      dispatch(setSuccess(translate("groups.addedToGroup", { name: group?.name || "" })));
    }
    reloadGroups();
  };

  const handleCreateGroup = async (name: string) => {
    await dispatch(createCompanyGroup({ name, customerIds: id ? [id] : [] }));
    dispatch(setSuccess(translate("groups.groupCreated", { name })));
    reloadGroups();
  };

  // Load own customer for isOwnProfile
  useEffect(() => {
    dispatch(getCustomer());
  }, []);

  // Data loading
  useEffect(() => {
    if (!id) return;
    dispatch(getCompany(id));
    reloadGroups();
    dispatch(getPartnershipWith(id));
    dispatch(getSentInvitations({ pageNo: 1, pageSize: 100, sortDesc: true }));
    // Fetch review summary for this company
    dispatch(fetchReviewSummary(id));
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: dt.pageBg,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader />
      </Box>
    );
  }

  if (!company) return null;

  return (
    <Box
      sx={{
        minHeight: 0,
        backgroundColor: dt.pageBg,
        backgroundImage: `radial-gradient(circle at 1px 1px, ${dt.pageDotColor} 1px, transparent 0)`,
        backgroundSize: "28px 28px",
        fontFamily: dt.font,
        overflowX: "hidden",
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
      }}
    >
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      {/* Unified Hero Card (full width, contact integrated) */}
      <Box
        sx={{
          maxWidth: 1100,
          margin: "0 auto",
          px: isPhone ? 2 : 3,
          pt: isPhone ? 2 : 3,
        }}
      >
        <CompanyHero
          company={company}
          isOwnProfile={isOwnProfile}
          isEditing={isEditing}
          editForm={editForm}
          onFieldChange={handleFieldChange}
          onEditToggle={handleEditToggle}
          onSave={handleSave}
          saving={saving}
          onLogoChange={handleLogoChange}
          groups={groupsList}
          companyId={id}
          partnershipStatus={partnershipStatus}
          onInvitePartner={() => setShowInviteDialog(true)}
          reviewSummary={reviewSummary}
          saveDropdown={
            <SaveDropdown
              companyId={id || ""}
              groups={groupsList}
              isSaved={isSaved}
              onToggleGroup={handleToggleGroup}
              onCreateGroup={handleCreateGroup}
              compact
            />
          }
        />
      </Box>

      {/* Logo crop modal */}
      {showLogoCrop && (
        <UploadImageCrop
          open={showLogoCrop}
          onClose={() => setShowLogoCrop(false)}
          file={logoFile}
          onChangeFile={handleLogoUploaded}
          cropVariant="square"
          maxFileSizeMb={2}
          minCropWidth={200}
          modalWidth="496px"
          aspectRatio={1}
        />
      )}

      {/* Inline Machinery Section */}
      <Box
        sx={{
          maxWidth: 1100,
          margin: "0 auto",
          px: isPhone ? 2 : 3,
          py: "20px",
          pb: "40px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <CompanyMachinery
          machines={machines}
          machineTypes={machineTypes}
          machineManufacturers={machineManufacturers}
          machinesQuery={machinesQuery}
          companyId={id || ""}
          customerType={company.customerType}
        />
      </Box>

      {/* Invite Partner Dialog */}
      {showInviteDialog && (
        <InvitePartnerDialog
          open={showInviteDialog}
          onClose={() => setShowInviteDialog(false)}
          onSuccess={(email?: string) => {
            setShowInviteDialog(false);
            dispatch(setSuccess(translate("partnerships:invite.successMessage", { email: email || "" })));
            if (id) {
              dispatch(getPartnershipWith(id));
              dispatch(getSentInvitations({ pageNo: 1, pageSize: 100, sortDesc: true }));
            }
          }}
          mode="profile"
          prefillEmail={company.email}
          prefillCompanyName={company.name}
          prefillCustomerType={company.customerType}
          viewerCustomerType={ownCustomer?.customerType}
        />
      )}
    </Box>
  );
};

export default Company;
